import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';
import { IntegratedCarePlan } from '../Models/CarePlans/IntegratedCarePlan.model';
import { MemberAssessment } from '../Models/Member/MemberAssessment.model';
import { ConsentDocumentValid } from '../Models/Consent/ConsentDocumentValid.model';
import { Request } from '../Models/Requests/Request.model';
import { DiagnosticStatus } from '../Models/Assessment/DiagnosticStatus.model';

const assessmentDashboardSlice = createSlice({
    name: 'assessmentDashboardSlice',
    initialState: {
        bhDiagnostic: new DiagnosticStatus(),
        carePlanStatus: new IntegratedCarePlan(),
        completedSurveys: [],
        consentDocumentValid: [] as ConsentDocumentValid[],
        isFetchingCarePlanStatus: false,
        isFetchingMemberPHQ9: false,
        memberPHQ9: new MemberAssessment(),
        memberRequests: [] as Request[],
    },
    reducers: {
        _setBHDiagnostic(state, action) {
            state.bhDiagnostic = action.payload;
        },
        _setCarePlanStatus(state, action) {
            state.carePlanStatus = action.payload;
        },
        _setCompletedSurveys(state, action) {
            state.completedSurveys = action.payload;
        },
        _setConsentDocumentValid(state, action) {
            state.consentDocumentValid = action.payload;
        },
        _setIsFetchingCarePlanStatus(state, action) {
            state.isFetchingCarePlanStatus = action.payload;
        },
        _setIsFetchingMemberPHQ9(state, action) {
            state.isFetchingMemberPHQ9 = action.payload;
        },
        _setMemberPHQ9(state, action) {
            state.memberPHQ9 = action.payload;
        },
        _setMemberRequests(state, action) {
            state.memberRequests = action.payload;
        },
    },
});

const {
    _setBHDiagnostic,
    _setCarePlanStatus,
    _setCompletedSurveys,
    _setConsentDocumentValid,
    _setIsFetchingCarePlanStatus,
    _setIsFetchingMemberPHQ9,
    _setMemberPHQ9,
    _setMemberRequests,
} = assessmentDashboardSlice.actions;

export const setBHDiagnostic =
    (bhDiagnostic: DiagnosticStatus): AppThunk =>
    async (dispatch) => {
        dispatch(_setBHDiagnostic(bhDiagnostic));
    };

export const setCarePlanStatus =
    (carePlanStatus: IntegratedCarePlan): AppThunk =>
    async (dispatch) => {
        dispatch(_setCarePlanStatus(carePlanStatus));
    };

export const setCompletedSurveys =
    (completedSurveys: any[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setCompletedSurveys(completedSurveys));
    };

export const setIsFetchingCarePlanStatus =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingCarePlanStatus(value));
    };

export const setIsFetchingMemberPHQ9 =
    (value: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingMemberPHQ9(value));
    };

export const setMemberPHQ9 =
    (memberPHQ9: MemberAssessment): AppThunk =>
    async (dispatch) => {
        dispatch(_setMemberPHQ9(memberPHQ9));
    };

export const setMemberRequests =
    (memberRequests: Request[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setMemberRequests(memberRequests));
    };

export const getLastCompletedBHDiagnosticByMemberId =
    (memberId: string, role: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/assessment/getlastcompletedbhdiagnosticbymemberid?`, { params: { memberId, role } });
        console.log('getBHDiagnosticByMemberId response', response.data);
        dispatch(setBHDiagnostic(response.data));
    };

export const getMemberRequests =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/request/member?`, { params: { memberId } });
        dispatch(setMemberRequests(response.data));
    };

export const getMemberSurveys =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/survey/getcompletedsurveys?`, { params: { memberId } });
        dispatch(setCompletedSurveys(response.data));
    };

export const getConsentFormDocumentByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/document/getconsentformbymemberid`, { params: { memberId } });
        let consentFormDocs = response.data as ConsentDocumentValid[];
        dispatch(_setConsentDocumentValid(consentFormDocs));
    };

export const fetchCarePlanByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingCarePlanStatus(true));
        const response = await axios.get(`${API_URL}/careplan/getcareplanbymemberid`, { params: { memberId } });
        dispatch(setCarePlanStatus(response.data));
        dispatch(setIsFetchingCarePlanStatus(false));
    };

export const fetchPHQ9ByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingMemberPHQ9(true));
        const response = await axios.get(`${API_URL}/assessment/getmemberassessment/13?memberId=${memberId}`, { params: { memberId } });
        dispatch(setMemberPHQ9(response.data));
        dispatch(setIsFetchingMemberPHQ9(false));
    };

export default assessmentDashboardSlice.reducer;
